import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Heart, Sparkles, Flame, ArrowRight } from "lucide-react";

// Tantra palette — intimate, distinct, sultry
// Deep Royal Purple #4B0082 · Dusty Rose Pink #D8A1A1 · Warm Metallic Gold #C5A059

const Tantric = () => {
  return (
    <div
      data-testid="page-tantric"
      className="bg-[#1a0033] text-[#F5E9D5]"
      style={{
        backgroundImage:
          "radial-gradient(ellipse at top, #4B0082 0%, #2A0048 45%, #17002B 100%)",
      }}
    >
      {/* Hero */}
      <section className="pt-40 pb-20 relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-10 relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-16 bg-[#C5A059]" aria-hidden />
            <p
              data-testid="tantric-eyebrow"
              className="text-xs tracking-[0.4em] uppercase text-[#C5A059]"
            >
              A Separate Professional Offering
            </p>
          </div>
          <h1
            data-testid="tantric-headline"
            className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#F5E9D5]"
          >
            Intimacy &amp; Erotic Exploration Using the{" "}
            <span className="italic text-[#D8A1A1]">Principles of Tantra</span>.
          </h1>
          <p className="mt-10 text-lg md:text-xl text-[#E8D9B8] leading-relaxed font-light max-w-3xl">
            A discreet, professional container for those drawn to explore
            intimacy, presence, embodiment, and connection through the
            philosophical lens of Tantra — with clarity, consent, and safety
            at the center.
          </p>
        </div>

        {/* Decorative sacred geometry accents */}
        <div
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full blur-3xl opacity-30"
          style={{ backgroundColor: "#D8A1A1" }}
          aria-hidden
        />
        <div
          className="absolute bottom-0 -left-20 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{ backgroundColor: "#C5A059" }}
          aria-hidden
        />
      </section>

      {/* Immersive sunset image — warm, embodied, alive */}
      <section
        data-testid="tantric-image-section"
        className="relative py-10 md:py-16"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div
            className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-md"
            style={{
              boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
              border: "1px solid rgba(197, 160, 89, 0.35)",
            }}
          >
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_grounded-wellness-3/artifacts/2b7cortl_Gemini_Generated_Image_211mtm211mtm211m.png"
              alt="Marissa at the edge of the sea, watching a golden sunset"
              className="w-full h-full object-cover"
              data-testid="tantric-sunset-image"
            />
            {/* subtle gradient for legibility */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(23,0,43,0) 55%, rgba(23,0,43,0.55) 100%)",
              }}
              aria-hidden
            />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <p
                className="font-serif italic text-xl md:text-2xl leading-snug max-w-xl"
                style={{ color: "#F5E9D5" }}
              >
                To be embodied is to feel the world touching you — and to
                choose, from that fullness, how you meet it back.
              </p>
              <div className="mt-4 flex items-center gap-3">
                <span
                  className="h-px w-10"
                  style={{ backgroundColor: "#C5A059" }}
                  aria-hidden
                />
                <span
                  className="text-[10px] tracking-[0.4em] uppercase"
                  style={{ color: "#C5A059" }}
                >
                  Presence · Embodiment · Choice
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Distinction */}
      <section
        data-testid="tantric-distinction"
        className="py-20 md:py-24 relative"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div
            className="p-10 md:p-12 rounded-md border relative overflow-hidden"
            style={{
              backgroundColor: "rgba(75, 0, 130, 0.35)",
              borderColor: "rgba(197, 160, 89, 0.4)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            }}
          >
            <div className="flex items-center gap-3 mb-8">
              <span
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "#C5A059", color: "#4B0082" }}
              >
                <ShieldCheck className="w-5 h-5" aria-hidden />
              </span>
              <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059]">
                Important Distinction
              </p>
            </div>
            <p className="font-serif text-2xl md:text-3xl leading-snug text-[#F5E9D5]">
              Clinical Yoga Therapy and Tantric Exploration are offered as{" "}
              <span className="italic text-[#D8A1A1]">
                completely separate, professional services
              </span>
              .
            </p>
            <p className="mt-6 text-[#E8D9B8] leading-relaxed font-light">
              For clients who wish to explore the benefits of integrating both
              practices, customized combination sessions can be arranged in a
              thoughtful, individualized way.
            </p>
          </div>
        </div>
      </section>

      {/* Ethos — Pillars */}
      <section className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-16">
            <p className="text-xs tracking-[0.35em] uppercase text-[#C5A059] mb-6">
              The Approach
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5E9D5] leading-tight">
              Grounded in <span className="italic text-[#D8A1A1]">presence</span>,{" "}
              consent, and self-inquiry.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Heart,
                title: "Consent-Centered",
                body: "Every session is shaped around your boundaries, readiness, and your own definition of exploration.",
              },
              {
                icon: Sparkles,
                title: "Embodied Presence",
                body: "Practices draw from Tantric philosophy as a framework for awareness, embodiment, and integration.",
              },
              {
                icon: Flame,
                title: "Discreet & Professional",
                body: "Not sexual service, not a substitute for licensed psychotherapy or medical care. A professional space, always.",
              },
            ].map((p, i) => (
              <div
                key={p.title}
                data-testid={`tantric-pillar-${i}`}
                className="p-8 rounded-md border transition-all duration-500 hover:-translate-y-1"
                style={{
                  backgroundColor: "rgba(75, 0, 130, 0.28)",
                  borderColor: "rgba(197, 160, 89, 0.3)",
                }}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-6"
                  style={{
                    backgroundColor: "rgba(197, 160, 89, 0.15)",
                    color: "#C5A059",
                    border: "1px solid rgba(197, 160, 89, 0.5)",
                  }}
                >
                  <p.icon className="w-5 h-5" aria-hidden />
                </div>
                <h3 className="font-serif text-2xl text-[#D8A1A1] mb-3 leading-snug">
                  {p.title}
                </h3>
                <p className="text-sm text-[#E8D9B8] leading-relaxed font-light">
                  {p.body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16 max-w-3xl space-y-6 text-[#E8D9B8] font-light leading-relaxed">
            <p>
              These sessions are shaped around your goals, boundaries, and
              readiness. There is no expectation, no agenda beyond your own
              exploration — only a professional space to become more embodied,
              more present, and more in relationship with your own capacity for
              intimacy.
            </p>
            <p>
              This offering draws from Tantric philosophy as a framework for
              awareness, embodiment, and integration. It is not sexual service
              and is not a substitute for licensed psychotherapy or medical
              care.
            </p>
          </div>
        </div>
      </section>

      {/* Discretion note + CTA */}
      <section className="pb-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div
            className="p-6 md:p-8 rounded-md flex items-start gap-4 mb-12"
            style={{
              backgroundColor: "rgba(216, 161, 161, 0.08)",
              border: "1px solid rgba(216, 161, 161, 0.3)",
            }}
          >
            <Heart
              className="w-5 h-5 flex-shrink-0 mt-1"
              style={{ color: "#D8A1A1" }}
              aria-hidden
            />
            <p className="text-sm text-[#E8D9B8] leading-relaxed font-light">
              To inquire about this offering, please reach out directly. All
              conversations are handled with the utmost discretion.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <Link to="/contact" data-testid="tantric-cta">
              <Button
                className="rounded-full px-10 py-6 h-auto text-base font-medium group border-0"
                style={{ backgroundColor: "#D8A1A1", color: "#4B0082" }}
              >
                Inquire Privately
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/" data-testid="tantric-back-home">
              <Button
                variant="outline"
                className="rounded-full px-10 py-6 h-auto bg-transparent"
                style={{
                  borderColor: "#C5A059",
                  color: "#C5A059",
                }}
              >
                Return to Yoga Therapy
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tantric;
